import {AnyAction, createAction, ThunkDispatch} from '@reduxjs/toolkit'

import {
    historyChange,
    changeLocation,
    openNewWindow,
    scrollTo,
    systemAlert,
    systemConfirm,
} from '@web/actions/application'
import {
    browserAlert as browserAlertUtil,
    delayedBrowserAlert,
    openNewTab,
    submitForm,
    submitFormToTarget,
} from '@web/helpers/browser'
import {isFirefox} from '@web/helpers/userAgent'

import {
    historyChangeEvent,
    locationChangeEvent,
    delayedLocationChangeEvent,
    scrollToEvent,
    systemConfirmEvent,
} from './browserEvent'

import type {Middleware} from '@reduxjs/toolkit'

interface NavigatorPayload {
    url: string
}

interface SubmitNavigatorPayload extends NavigatorPayload {
    fields?: {
        [name: string]: string
    }
}

export const formSubmitToAnotherPage = createAction('formSubmitToAnotherPage', (payload: SubmitNavigatorPayload) => {
    return {payload}
})

interface BrowserAlertPayload {
    message: string
    browserDependentDelay?: boolean
}

export const browserAlert = createAction(
    'browserAlert',
    ({message, browserDependentDelay = true}: BrowserAlertPayload) => {
        return {
            payload: {
                message,
                delay: browserDependentDelay && isFirefox,
            },
        }
    },
)

interface BrowserConfirmPayload {
    message: string
    okay: () => void
    cancel?: () => void
}

export const browserConfirm = createAction('browserConfirm', (payload: BrowserConfirmPayload) => {
    return {payload}
})

export const asyncConfirm = <StateType>(dispatch: ThunkDispatch<StateType, unknown, AnyAction>, message: string) => {
    return new Promise<boolean>((resolve) => {
        dispatch(
            browserConfirm({
                message,
                okay: () => resolve(true),
                cancel: () => resolve(false),
            }),
        )
    })
}

const browserConfirmEvent = (payload: BrowserConfirmPayload) => {
    if (window.confirm(payload.message)) {
        payload.okay()
    } else {
        payload.cancel?.()
    }
}

interface FormSubmitPayload extends SubmitNavigatorPayload {
    method?: 'GET' | 'POST'
    target: string
    success: () => void
    fail: () => void
}

export const formSubmitToIframe = createAction('formSubmitToIframe', (payload: FormSubmitPayload) => {
    return {payload}
})

export const closeWindowAction = createAction('closeWindowAction', () => {
    return {payload: null}
})

const closeWindowEvent = () => {
    window.close()
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const browserEventEmitter: Middleware = () => (next) => (action: any) => {
    const {type, payload} = action

    switch (type) {
        case formSubmitToAnotherPage.type:
            submitForm(payload)
            break
        case browserAlert.type:
            payload.delay ? delayedBrowserAlert(payload) : browserAlertUtil(payload)
            break
        case systemAlert.type:
            payload.delay ? delayedBrowserAlert(payload) : browserAlertUtil(payload)
            break
        case browserConfirm.type:
            browserConfirmEvent(payload)
            break
        case systemConfirm.type:
            systemConfirmEvent(payload)
            break
        case formSubmitToIframe.type:
            submitFormToTarget(payload)
            break
        case openNewWindow.type:
            openNewTab({url: payload.url})
            break
        case closeWindowAction.type:
            closeWindowEvent()
            break
        case scrollTo.type:
            scrollToEvent(payload)
            break
        case changeLocation.type:
            payload.delay ? delayedLocationChangeEvent(payload) : locationChangeEvent(payload)
            break
        case historyChange.type:
            historyChangeEvent(payload)
            break
    }

    return next(action)
}

export default browserEventEmitter
